import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, query, where, documentId, getDoc, doc } from 'firebase/firestore'

import { db as firestore } from '@/firebase' // Corrected import path

// Define the expected structure of a proficiency standard document
export interface ProficiencyStandard {
  id: string
  name: string
  description: string
  levelEnums: string[] // e.g., ["NM", "NH", "IL", "IM", "IH"]
  levels: {
    id: string // e.g., "NM"
    description: string
    title: string // Add title field
    // descriptor_feedback_instructions?: string; // Optional, based on spec
  }[]
  // Add any other fields that exist on the document
}

const PROFICIENCY_STANDARDS_COLLECTION = 'proficiency_standards'

// Function to fetch proficiency standards
const fetchProficiencyStandards = async ({ all = false }: { all?: boolean } = {}): Promise<ProficiencyStandard[]> => {
  try {
    const standardsCollectionRef = collection(firestore, PROFICIENCY_STANDARDS_COLLECTION)
    // Fetch only 'actfl' and 'wida' documents
    const q = all ? query(standardsCollectionRef) : query(standardsCollectionRef, where(documentId(), 'in', ['actfl', 'wida']))
    // const q = query(standardsCollectionRef);
    const querySnapshot = await getDocs(q)
    const standards = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as Omit<ProficiencyStandard, 'id'>),
    }))

    // // Fallback logic if no standards are fetched or if specific language-based fallbacks are needed
    // if (standards.length === 0) {
    //   if (language === 'en') {
    //     // Attempt to fetch WIDA specifically, or return a mock/default WIDA structure
    //     // For now, returning an empty array and letting the UI handle "no standards"
    //     console.warn('No proficiency standards found, WIDA fallback for EN language needs implementation if direct fetch is required.');
    //   } else {
    //     // Attempt to fetch ACTFL specifically, or return a mock/default ACTFL structure
    //     console.warn('No proficiency standards found, ACTFL fallback for other languages needs implementation if direct fetch is required.');
    //   }
    //   // Depending on requirements, you might return a predefined fallback object here.
    //   // For example: return [{ id: 'wida', name: 'WIDA (Fallback)', ... }]
    // }
    return standards // Return the fetched standards
  } catch (error) {
    console.error('Error fetching proficiency standards:', error)
    throw new Error('Failed to fetch proficiency standards')
  }
}

interface UseProficiencyStandardsProps {
  language?: string // Current card language to determine fallback or filtering
  enabled?: boolean // To enable/disable the query
  all?: boolean // To fetch all standards
}

export const useProficiencyStandards = ({ language, enabled = true, all = false }: UseProficiencyStandardsProps = {}) => {
  return useQuery({
    queryKey: ['proficiencyStandards', language],

    // Query key, includes language if standards differ by language
    queryFn: () => fetchProficiencyStandards({ all }),

    enabled,

    // 5 minutes
    staleTime: 1000 * 60 * 5,

    // 30 minutes
    // Add more react-query options as needed (e.g., onSuccess, onError, placeholderData)
    // Placeholder data can be used for the WIDA/ACTFL fallback if direct fetch for them isn't done
    // placeholderData: () => {
    //   if (language === 'en') return [{ id: 'wida', name: 'WIDA (Default)', ... }];
    //   return [{ id: 'actfl', name: 'ACTFL (Default)', ... }];
    // }
    gcTime: 1000 * 60 * 30,
  })
}

const fetchProficiencyStandard = async (id: string, level: string): Promise<ProficiencyStandard> => {
  const standardsCollectionRef = collection(firestore, PROFICIENCY_STANDARDS_COLLECTION)
  const q = query(standardsCollectionRef, where(documentId(), '==', id), where('levels.id', '==', level))
  const querySnapshot = await getDocs(q)

  return querySnapshot.docs[0].data() as ProficiencyStandard
}

export const useProficiencyStandardLevel = (id: string, level: string) => {
  return useQuery({
    queryKey: ['proficiencyStandard', id, level],
    queryFn: () => fetchProficiencyStandard(id, level),
    enabled: !!id && !!level,

    // 5 minutes
    staleTime: 1000 * 60 * 5,

    // 30 minutes
    gcTime: 1000 * 60 * 30,
  })
}


const fetchSingleProficiencyStandard = async (id: string): Promise<ProficiencyStandard> => {
  const standardsCollectionRef = collection(firestore, PROFICIENCY_STANDARDS_COLLECTION)
  const docRef = doc(standardsCollectionRef, id)
  const docSnapshot = await getDoc(docRef)
  return docSnapshot.data() as ProficiencyStandard
}

export const useProficiencyStandard = (id: string) => {
  return useQuery({
    queryKey: ['proficiencyStandard', id],
    queryFn: () => fetchSingleProficiencyStandard(id),
    enabled: !!id,
  })
}
